import { useRef } from 'react';
import { CSSTransition } from 'react-transition-group';
import styled from 'styled-components';

import { ClosingIndicator } from '@xing-com/closing-indicator';
import {
  mediaConfined,
  mediaSmallOrTiny,
  zIndexElevatedLayer4,
} from '@xing-com/layout-tokens';
import {
  cornerRadiusXL,
  dropShadowM,
  motionEasingEnter,
  motionEasingLeave,
  motionTimeM,
  motionTimeNumberM,
  scale005,
  scale180,
  scale410,
  space3XL,
  spaceL,
  xdlColorBackgroundQuaternary,
  xdlColorModalBorder,
} from '@xing-com/tokens';

type Props = {
  show?: boolean;
  children: React.ReactNode;
  onClose?: () => void;
  onEntered?: () => void;
};

export const AiFeedbackCard: React.FC<Props> = ({
  show,
  children,
  onClose,
  onEntered,
}) => {
  const cardRef = useRef<HTMLDivElement>(null);

  return (
    <CSSTransition
      appear={show}
      classNames={TRANSITION_NAME}
      in={show}
      key="ai-feedback-card"
      mountOnEnter
      timeout={+motionTimeNumberM}
      unmountOnExit
      onEntered={onEntered}
      nodeRef={cardRef}
    >
      <Card ref={cardRef}>
        {children}
        <CloseButton size="large" onClick={onClose} />
      </Card>
    </CSSTransition>
  );
};

const TRANSITION_NAME = 'slide';

const Card = styled.div`
  position: fixed;
  display: flex;
  flex-direction: column;
  padding: ${spaceL};
  gap: ${spaceL};
  background-color: ${xdlColorBackgroundQuaternary};
  z-index: calc(${zIndexElevatedLayer4} + 50);
  box-shadow: ${dropShadowM};
  text-align: center;
  align-items: center;
  border-radius: ${cornerRadiusXL};

  @media ${mediaSmallOrTiny} {
    --bottom: 0%;
    left: 0;
    right: 0;
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
  }

  @media ${mediaSmallOrTiny} and (prefers-color-scheme: dark) {
    border-top: ${scale005} solid ${xdlColorModalBorder};
    box-shadow: 0px -4px 16px 3px rgba(22, 28, 36, 0.6);
  }

  @media ${mediaConfined} {
    // we add the SA fixed banner height
    --bottom: calc(${space3XL} + ${scale180});
    right: ${space3XL};
    width: ${scale410};
  }

  @media ${mediaConfined} and (prefers-color-scheme: dark) {
    border: ${scale005} solid ${xdlColorModalBorder};
    box-shadow: 0px 4px 16px 3px rgba(22, 28, 36, 0.6);
  }

  /* Animation from XDS banner */
  bottom: var(--bottom);
  --height100: calc(100% + var(--bottom));
  --height99: calc(99.9% + var(--bottom));
  transform: translate3d(0, var(--height100), 0);

  &.${TRANSITION_NAME}-enter {
    transform: translate3d(0, var(--height99), 0);
  }

  &.${TRANSITION_NAME}-enter-active {
    transform: translate3d(0, 0%, 0);
    transition-duration: ${motionTimeM};
    transition-property: transform;
    transition-timing-function: ${motionEasingEnter};
  }

  &.${TRANSITION_NAME}-enter-done {
    transform: translate3d(0, 0%, 0);
    transition-duration: ${motionTimeM};
    transition-property: transform;
    transition-timing-function: ${motionEasingEnter};
  }

  &.${TRANSITION_NAME}-exit {
    transform: translate3d(0, 0%, 0);
  }

  &.${TRANSITION_NAME}-exit-active {
    transform: translate3d(0, var(--height99), 0);
    transition-duration: ${motionTimeM};
    transition-property: transform;
    transition-timing-function: ${motionEasingLeave};
  }

  &.${TRANSITION_NAME}-exit-done {
    transform: translate3d(0, var(--height100), 0);
    transition-duration: ${motionTimeM};
    transition-property: transform;
    transition-timing-function: ${motionEasingLeave};
  }
`;

const CloseButton = styled(ClosingIndicator)`
  inset-block-start: 0;
  inset-inline-end: 0;
  position: absolute;
  z-index: 1;

  &::after {
    background-color: transparent;
  }
`;
